/**
 * Node metadata: names and content types, encrypted under the node's own key.
 *
 * The server stores `name_enc` and `mime_enc` and can read neither, which has
 * one consequence that shapes the whole app: **there is no server-side sort or
 * search**. Listings come back in id order and the client decrypts, sorts and
 * filters. That is fine at the scale a person's own drive reaches and is
 * explicitly a scaling limit — see docs/BACKLOG.md.
 *
 * `name_hmac` is the exception that makes one server-side check possible. It is
 * HMAC-SHA256 of the lowercased name under the *parent's* key, so the server can
 * compare two siblings for equality — enough to enforce "no duplicate names in
 * a folder" — while holding neither the name nor the key. Keying on the parent
 * rather than the node is what makes the comparison meaningful: siblings share
 * a parent, so their HMACs are computed under the same key and are comparable,
 * while the same filename in two different folders produces unrelated values
 * and leaks nothing across folders.
 */

import {
  aesDecryptString,
  aesEncryptString,
  hmacSha256,
  toBase64,
  utf8,
  type Bytes
} from './primitives';

const NAME_AAD = utf8('mauvely.cloud.meta.name');
const MIME_AAD = utf8('mauvely.cloud.meta.mime');

export const encryptName = (nodeKey: CryptoKey, name: string): Promise<string> =>
  aesEncryptString(nodeKey, name, NAME_AAD);

export const decryptName = (nodeKey: CryptoKey, enc: string): Promise<string> =>
  aesDecryptString(nodeKey, enc, NAME_AAD);

export const encryptMime = (nodeKey: CryptoKey, mime: string): Promise<string> =>
  aesEncryptString(nodeKey, mime, MIME_AAD);

export const decryptMime = (nodeKey: CryptoKey, enc: string): Promise<string> =>
  aesDecryptString(nodeKey, enc, MIME_AAD);

/**
 * The sibling-uniqueness token for a name inside a folder.
 *
 * Case- and whitespace-normalised before hashing, so "Report.pdf" and
 * "report.pdf " collide the way a user expects them to — matching the
 * case-insensitive behaviour of the desktop filesystems these files came from.
 */
export async function nameHmac(parentKey: CryptoKey, name: string): Promise<string> {
  return toBase64(await hmacSha256(parentKey, utf8(normaliseName(name))));
}

/** Trim, collapse internal whitespace, lowercase. Unicode-normalised so two
 *  visually identical names built from different code points do not both
 *  survive — NFC is what filesystems and users assume. */
export const normaliseName = (name: string): string =>
  name.normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Reject names that would be a problem once decrypted and handed to a
 * filesystem, a Content-Disposition header or a path join.
 *
 * Validated client-side because the server cannot see the name at all — which
 * means this is a usability guard, not a security boundary. Nothing downstream
 * may treat a decrypted name as trusted: the download path sets a filename via
 * an encoded `Content-Disposition` and never interpolates one into a path.
 */
export function assertValidName(name: string): string {
  const trimmed = name.trim();

  if (!trimmed) throw new Error('A name is required.');
  if (trimmed.length > 255) throw new Error('That name is too long.');
  if (trimmed === '.' || trimmed === '..') throw new Error('That name is not allowed.');
  // Path separators and the C0 control range: neither can appear in a filename
  // on any platform this syncs to, and both are classic traversal material.
  if (/[/\\]/.test(trimmed)) throw new Error('A name cannot contain a slash.');
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(trimmed)) throw new Error('That name contains invalid characters.');

  return trimmed;
}

/** Bytes of a decrypted name, for the callers that need to pad or size it. */
export const nameBytes = (name: string): Bytes => utf8(name);
